/**
 * Launcher V2 live-QA round 2, Batch 5 (D1) — display-only sanitized HTML
 * render, for Gong step bodies that are real HTML (email-family step types —
 * see `gongStepBodyFormat` in `../gong-flow-picker-api.ts`) shown in a
 * READ-ONLY context: `SamplePreviewCard`'s body cell and both columns of
 * `CopyDiffView`.
 *
 * This is the read counterpart to `RichHtmlField`'s edit path — same
 * dependency (`dompurify`), same "sanitize is the trust boundary, not
 * detection" posture (`web/app-home/src/components/signals/descriptionContent.tsx`'s
 * `sanitizeDescriptionHtml` is the closest existing precedent: a plain
 * `dangerouslySetInnerHTML` div, no `contentEditable`, sanitized on every
 * render since there is no seed-vs-live-edit distinction to protect against
 * here — nothing rendered through this component is ever user-editable).
 *
 * NEVER pair this with `contentEditable` — that combination is the
 * lint-enforced defect `RichHtmlField`'s own module doc calls out
 * (`eslint-rules/no-contenteditable-with-inner-html.js`). This component has
 * no editable variant; if a caller needs one, that's `RichHtmlField`, not this.
 */

import DOMPurify from 'dompurify';

export interface SanitizedHtmlProps {
  /** Raw (untrusted) HTML string — sanitized at render, every render. */
  html: string;
  className?: string;
}

/**
 * The one sanitize call every caller in this file (and `CopyDiffView.tsx`'s
 * `OneColumnValue`, which needs the sanitized result itself to decide
 * emptiness — Codex adversarial-review finding 3, Batch 5 fix-up) goes
 * through. Exported so a caller that needs to inspect the sanitized output
 * (not just render it) can reuse the exact same pass instead of hand-rolling
 * a second DOMPurify call.
 */
export function sanitizeHtml(html: string): string {
  // Every link opens in a new tab, so a rep never navigates away from Terra
  // (Clay's own HTML may carry `target="_top"` / `rel="opener"`: overwritten).
  // DOMPurify hooks are global to the shared instance, so this one is
  // registered for this synchronous call only — `RichHtmlField` and the other
  // `dompurify` users are never affected.
  DOMPurify.addHook('afterSanitizeAttributes', openLinkInNewTab);
  try {
    return DOMPurify.sanitize(html);
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', openLinkInNewTab);
  }
}

function openLinkInNewTab(node: Element): void {
  if (node.tagName !== 'A') return;
  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noopener noreferrer');
}

/**
 * Escapes the standard HTML-special characters. For a `filled` template
 * segment (a resolved CRM/roster value) joined into an HTML string ahead of
 * `sanitizeHtml`/`SanitizedHtml` — see `SequencesStep.tsx`'s
 * `SamplePreviewCard` (Codex adversarial-review finding 1, Batch 5 fix-up).
 * DOMPurify's default policy retains ordinary markup (images, links, inline
 * styles), so joining an untrusted value in unescaped and relying on
 * DOMPurify alone would let a messy CRM field (or a deliberately hostile one)
 * inject a live element — escaping first is what actually closes that gap;
 * DOMPurify still runs after as defense-in-depth.
 */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function SanitizedHtml({ html, className }: SanitizedHtmlProps) {
  return (
    <div
      className={className}
      // Sanitized via `sanitizeHtml` immediately above — the only way HTML
      // reaches this prop.
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}
